import dagre from "@dagrejs/dagre";
import {
  FlowchartDirection,
  FlowchartNodeShape,
  type FlowchartNode,
  type FlowchartSubgraph,
} from "./parse-mermaid-flowchart";
import type { ResolvedFlowchart } from "./resolve-flowchart";
import {
  DAGRE_EDGESEP,
  DAGRE_MARGIN_X,
  DAGRE_MARGIN_Y,
  DAGRE_NODESEP,
  DAGRE_RANKSEP,
  DEFAULT_ACCENT_BY_SHAPE,
  EDGE_WEIGHT_BY_KIND,
  GROUP_LABEL_HEIGHT,
  GROUP_NODE_PADDING_X,
  GROUP_NODE_PADDING_Y,
} from "./flowchart-constants";
import { packDisconnectedComponents } from "./flowchart-component-packing";
import type { NodeBox, NodeSize } from "./flowchart-layout-types";
import {
  getNodeShape,
  getNodeSize,
  getSubgraphDepth,
  getSubgraphSeedSize,
} from "./flowchart-layout-sizing";
import {
  resolvePresentation,
  type ResolvedPresentation,
} from "./flowchart-layout-presentation";

export interface DagreGraphOptions {
  multigraph: boolean;
  compound: boolean;
}

interface DagreGraphData {
  rankdir: FlowchartDirection;
  nodesep: number;
  ranksep: number;
  edgesep: number;
  marginx: number;
  marginy: number;
  width?: number;
  height?: number;
}

interface DagreNodeData {
  width: number;
  height: number;
  paddingX?: number;
  paddingY?: number;
  label?: string;
  x?: number;
  y?: number;
}

interface DagreEdgeData {
  weight: number;
  minlen: number;
}

interface DagreNodeLayoutValues {
  width?: number;
  height?: number;
  x?: number;
  y?: number;
}

type FlowchartDagreGraph = dagre.graphlib.Graph<
  DagreGraphData,
  DagreNodeData,
  DagreEdgeData
>;

interface FlowchartDagreLayout {
  dagreGraph: FlowchartDagreGraph;
  subgraphSizeById: Map<string, NodeSize>;
  nodeSizeById: Map<string, NodeSize>;
}

export interface BoxAndPresentationMaps {
  nodeBoxesById: Map<string, NodeBox>;
  presentationById: Map<string, ResolvedPresentation>;
}

export const FLOWCHART_DAGRE_GRAPH_OPTIONS: DagreGraphOptions = {
  multigraph: true,
  compound: true,
};

function isFinitePositive(value: number | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

function isFiniteNumber(value: number | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function getPositiveDimension(input: {
  value: number | undefined;
  fallback: number;
}): number {
  if (!isFinitePositive(input.value)) {
    return input.fallback;
  }
  return Math.max(input.value, input.fallback);
}

function getCenterCoordinate(input: {
  value: number | undefined;
  fallback: number;
}): number {
  if (!isFiniteNumber(input.value)) {
    return input.fallback;
  }
  return input.value;
}

function getDagreNodeLayoutValues(
  dagreNode: DagreNodeData | undefined,
): DagreNodeLayoutValues {
  if (!dagreNode) {
    return {};
  }
  return {
    width: dagreNode.width,
    height: dagreNode.height,
    x: dagreNode.x,
    y: dagreNode.y,
  };
}

function getNodeBox(input: {
  dagreGraph: FlowchartDagreGraph;
  id: string;
  fallbackSize: NodeSize;
}): NodeBox {
  const dagreNode = getDagreNodeLayoutValues(input.dagreGraph.node(input.id));
  const width = getPositiveDimension({
    value: dagreNode.width,
    fallback: input.fallbackSize.width,
  });
  const height = getPositiveDimension({
    value: dagreNode.height,
    fallback: input.fallbackSize.height,
  });
  const centerX = getCenterCoordinate({
    value: dagreNode.x,
    fallback: width / 2,
  });
  const centerY = getCenterCoordinate({
    value: dagreNode.y,
    fallback: height / 2,
  });
  return {
    x: centerX - width / 2,
    y: centerY - height / 2,
    width,
    height,
  };
}

function getLayoutEndpoint(input: {
  id: string;
  resolved: ResolvedFlowchart;
}): string {
  const subgraph = input.resolved.subgraphs.find(
    (candidate) => candidate.id === input.id,
  );
  if (!subgraph) {
    return input.id;
  }

  const directNode = input.resolved.nodes.find(
    (node) => node.parent === subgraph.id,
  );
  if (directNode) {
    return directNode.id;
  }

  const directSubgraph = input.resolved.subgraphs.find(
    (candidate) => candidate.parent === subgraph.id,
  );
  return directSubgraph
    ? getLayoutEndpoint({ id: directSubgraph.id, resolved: input.resolved })
    : input.id;
}

export function getGraphSize(input: {
  dagreGraph: FlowchartDagreGraph;
  boxes: readonly NodeBox[];
}): NodeSize {
  const graphData = input.dagreGraph.graph();
  const widthFromBoxes = input.boxes.reduce(
    (width, box) => Math.max(width, box.x + box.width),
    0,
  );
  const heightFromBoxes = input.boxes.reduce(
    (height, box) => Math.max(height, box.y + box.height),
    0,
  );
  return {
    width: isFinitePositive(graphData.width) ? graphData.width : widthFromBoxes,
    height: isFinitePositive(graphData.height)
      ? graphData.height
      : heightFromBoxes,
  };
}

function createDagreGraph(): FlowchartDagreGraph {
  const dagreGraph = new dagre.graphlib.Graph<
    DagreGraphData,
    DagreNodeData,
    DagreEdgeData
  >(FLOWCHART_DAGRE_GRAPH_OPTIONS);
  dagreGraph.setGraph({
    rankdir: FlowchartDirection.TopToBottom,
    nodesep: DAGRE_NODESEP,
    ranksep: DAGRE_RANKSEP,
    edgesep: DAGRE_EDGESEP,
    marginx: DAGRE_MARGIN_X,
    marginy: DAGRE_MARGIN_Y,
  });
  dagreGraph.setDefaultEdgeLabel(() => ({ weight: 1, minlen: 1 }));
  return dagreGraph;
}

function addSubgraphsToGraph(input: {
  dagreGraph: FlowchartDagreGraph;
  subgraphs: readonly FlowchartSubgraph[];
}): Map<string, NodeSize> {
  const sizeById = new Map<string, NodeSize>();
  input.subgraphs.forEach((subgraph) => {
    const size = getSubgraphSeedSize(subgraph);
    sizeById.set(subgraph.id, size);
    input.dagreGraph.setNode(subgraph.id, {
      ...size,
      label: subgraph.label ?? subgraph.id,
      paddingX: GROUP_NODE_PADDING_X,
      paddingY: GROUP_NODE_PADDING_Y + GROUP_LABEL_HEIGHT,
    });
  });
  return sizeById;
}

function addNodesToGraph(input: {
  dagreGraph: FlowchartDagreGraph;
  nodes: readonly FlowchartNode[];
}): Map<string, NodeSize> {
  const sizeById = new Map<string, NodeSize>();
  input.nodes.forEach((node) => {
    const size = getNodeSize(node);
    sizeById.set(node.id, size);
    input.dagreGraph.setNode(node.id, size);
  });
  return sizeById;
}

function addParentsToGraph(input: {
  dagreGraph: FlowchartDagreGraph;
  nodes: readonly FlowchartNode[];
  subgraphs: readonly FlowchartSubgraph[];
}): void {
  input.subgraphs.forEach((subgraph) => {
    if (subgraph.parent && input.dagreGraph.hasNode(subgraph.parent)) {
      input.dagreGraph.setParent(subgraph.id, subgraph.parent);
    }
  });
  input.nodes.forEach((node) => {
    if (node.parent && input.dagreGraph.hasNode(node.parent)) {
      input.dagreGraph.setParent(node.id, node.parent);
    }
  });
}

function addEdgesToGraph(input: {
  dagreGraph: FlowchartDagreGraph;
  resolved: ResolvedFlowchart;
}): void {
  input.resolved.edges.forEach((edge) => {
    const layoutSource = getLayoutEndpoint({
      id: edge.from,
      resolved: input.resolved,
    });
    const layoutTarget = getLayoutEndpoint({
      id: edge.to,
      resolved: input.resolved,
    });
    if (layoutSource === layoutTarget) {
      return;
    }
    input.dagreGraph.setEdge(
      layoutSource,
      layoutTarget,
      {
        weight: EDGE_WEIGHT_BY_KIND[edge.kind],
        minlen: 1,
      },
      edge.id,
    );
  });
}

export function getOrderedSubgraphs(input: {
  resolved: ResolvedFlowchart;
  subgraphById: ReadonlyMap<string, FlowchartSubgraph>;
}): FlowchartSubgraph[] {
  return [...input.resolved.subgraphs].sort((left, right) => {
    const leftDepth = getSubgraphDepth({
      subgraph: left,
      subgraphById: input.subgraphById,
    });
    const rightDepth = getSubgraphDepth({
      subgraph: right,
      subgraphById: input.subgraphById,
    });
    return leftDepth - rightDepth;
  });
}

export function createLaidOutDagreGraph(input: {
  resolved: ResolvedFlowchart;
}): FlowchartDagreLayout {
  const dagreGraph = createDagreGraph();
  dagreGraph.setGraph({
    rankdir: input.resolved.direction,
    nodesep: DAGRE_NODESEP,
    ranksep: DAGRE_RANKSEP,
    edgesep: DAGRE_EDGESEP,
    marginx: DAGRE_MARGIN_X,
    marginy: DAGRE_MARGIN_Y,
  });

  const subgraphSizeById = addSubgraphsToGraph({
    dagreGraph,
    subgraphs: input.resolved.subgraphs,
  });
  const nodeSizeById = addNodesToGraph({
    dagreGraph,
    nodes: input.resolved.nodes,
  });
  addParentsToGraph({
    dagreGraph,
    nodes: input.resolved.nodes,
    subgraphs: input.resolved.subgraphs,
  });
  addEdgesToGraph({ dagreGraph, resolved: input.resolved });
  dagre.layout(dagreGraph);

  return { dagreGraph, subgraphSizeById, nodeSizeById };
}

function addSubgraphLayoutEntries(input: {
  resolved: ResolvedFlowchart;
  dagreGraph: FlowchartDagreGraph;
  orderedSubgraphs: readonly FlowchartSubgraph[];
  subgraphSizeById: ReadonlyMap<string, NodeSize>;
  nodeBoxesById: Map<string, NodeBox>;
  presentationById: Map<string, ResolvedPresentation>;
}): void {
  input.orderedSubgraphs.forEach((subgraph) => {
    const fallbackSize =
      input.subgraphSizeById.get(subgraph.id) ?? getSubgraphSeedSize(subgraph);
    const box = getNodeBox({
      dagreGraph: input.dagreGraph,
      id: subgraph.id,
      fallbackSize,
    });
    input.nodeBoxesById.set(subgraph.id, box);
    input.presentationById.set(
      subgraph.id,
      resolvePresentation({
        classNames: subgraph.classNames,
        inlineStyle: subgraph.inlineStyle,
        classDefs: input.resolved.classDefs,
        defaultAccent: DEFAULT_ACCENT_BY_SHAPE[FlowchartNodeShape.Process],
        includeDefaultClass: false,
      }),
    );
  });
}

function addNodeLayoutEntries(input: {
  resolved: ResolvedFlowchart;
  dagreGraph: FlowchartDagreGraph;
  nodeSizeById: ReadonlyMap<string, NodeSize>;
  nodeBoxesById: Map<string, NodeBox>;
  presentationById: Map<string, ResolvedPresentation>;
}): void {
  input.resolved.nodes.forEach((node) => {
    const shape = getNodeShape(node);
    const fallbackSize = input.nodeSizeById.get(node.id) ?? getNodeSize(node);
    const box = getNodeBox({
      dagreGraph: input.dagreGraph,
      id: node.id,
      fallbackSize,
    });
    input.nodeBoxesById.set(node.id, box);
    input.presentationById.set(
      node.id,
      resolvePresentation({
        classNames: node.classNames,
        inlineStyle: node.inlineStyle,
        classDefs: input.resolved.classDefs,
        defaultAccent: DEFAULT_ACCENT_BY_SHAPE[shape],
        includeDefaultClass: true,
      }),
    );
  });
}

export function buildBoxAndPresentationMaps(input: {
  resolved: ResolvedFlowchart;
  dagreGraph: FlowchartDagreGraph;
  orderedSubgraphs: readonly FlowchartSubgraph[];
  subgraphSizeById: ReadonlyMap<string, NodeSize>;
  nodeSizeById: ReadonlyMap<string, NodeSize>;
}): BoxAndPresentationMaps {
  const nodeBoxesById = new Map<string, NodeBox>();
  const presentationById = new Map<string, ResolvedPresentation>();

  addSubgraphLayoutEntries({
    resolved: input.resolved,
    dagreGraph: input.dagreGraph,
    orderedSubgraphs: input.orderedSubgraphs,
    subgraphSizeById: input.subgraphSizeById,
    nodeBoxesById,
    presentationById,
  });
  addNodeLayoutEntries({
    resolved: input.resolved,
    dagreGraph: input.dagreGraph,
    nodeSizeById: input.nodeSizeById,
    nodeBoxesById,
    presentationById,
  });

  packDisconnectedComponents({ resolved: input.resolved, nodeBoxesById });

  return { nodeBoxesById, presentationById };
}
